import { IConstraint } from '@se/se.platform.base';
import { SeSideComponentType } from '@se/platform';
import { IBindingRule } from './constraints';
import { IPlaceholders } from './data';
import { IServerDataSource } from './datasources';
import { IControl } from './metadata-controls';

/*
 * Indica se l'header della card va visualizzato nel rollup
 * */
export enum CustomPageVisibility {
    auto = 0, // Senza controlli all'interno non visualizzo la card
    visible = 1, // Visualizzo la card anche se il suo contenuto è composto da soli pannelli figli (con e senza contrlli)
    hidden = 2, // Non visalizzo mai la card
}

export enum CustomPagePathDescriptionVisibility {
    visible = 0, // La descrizione sarà presente nell'header delle card figlie
    hidden = 1, // La descrizione NON sarà presente nell'header delle card figlie
}

export interface ICustomPage {
    id: string;
    name: string;
    idAddMenuDataSource?: string;
    autoNormalValues: boolean; //potrebbero essere richiesti al server al click del pulsante della toolbar
    description: string;
    templateCode: string;
    dynamicPanelItems: IDynamicPanelItem[];
    fixedPanelItems: IFixedPanelItem[];
    modules: string[];
    leftSliderClosed?: boolean;
    rightSliderClosed?: boolean;
    rightSliderEnabled?: boolean;
    seSideComponents?: SeSideComponentType[];
    imaging?: boolean;
    additionalSheets?: IAdditionalSheetSettings[];
    patientHistoryActors: string[];
}

export interface IPanelItem {
    id: string;
    header: string;
    pathDescriptionVisibility: CustomPagePathDescriptionVisibility;
    visibility: CustomPageVisibility;
    controlPanel: IControlPanel;
}

export interface IStaticPanelItem extends IPanelItem {
    // I figli di questo panel.
    dynamicPanelItems: IDynamicPanelItem[];
    // I figli di questo pannello che devono essere renderizzati orizzontalmente.
    horizontalPanelItems?: IHorizontalPanelItem[];
    // Pannelli di dettaglio, mostrati come expander.
    detailPanelItems: IDetailPanelItem[];
    // Constraint legati al panel.
    constraints?: IBindingRule[];
    // Per tener traccia di quale entity ha creato questo pannello.
    createdBy?: string;
}

export enum DataBindingMode {
    Single = 0, // Pannello singolo. Es: CORO, PTCA, ...
    Multiple = 1, // Pannello multiplo. Es: Lesioni PTCA, ...
    Property = 2, // Pannello multiplo con prevalorizzazione. Es: Lesioni PTCA con sede prevalorizzata
    PropertyExclusive = 3, // Pannello multiplo con prevalorizzazione con voci univoche. Es: Lesioni PTCA con al massimo una lesione per ogni sede.
    OnRequest = 4, // Pannello singolo, sempre nascosto, visualizzato a richiesta dell'utente. Richiede binding nullo.
}

export interface IVirtualPanel {
    dataBinding: string;
    dataBindingMode: DataBindingMode;
    idDataSource: string;
    readonly: boolean;
    addButtonTooltip?: string | null;
    removeButtonTooltip?: string | null;
    defaultMultiplePanelsNumber?: number;
    showIndex?: boolean;
}

export interface IVirtualPanelItem extends IStaticPanelItem {
    virtualPanel: IVirtualPanel;
}

export type IDynamicPanelItem = IStaticPanelItem | IVirtualPanelItem;

export function isStaticPanel(panel: IDynamicPanelItem): panel is IVirtualPanelItem {
    return !isVirtualPanel(panel);
}

export function isHorizontalContainerPanel(panel: IDynamicPanelItem) {
    return panel.horizontalPanelItems && panel.horizontalPanelItems.length > 0;
}

export function isVirtualPanel(panel: IDynamicPanelItem): panel is IVirtualPanelItem {
    return 'virtualPanel' in panel && !!panel.virtualPanel;
}

export enum VirtualPanelSelectionMode {
    Single = 0,
    Multiple = 1,
}

export type IFixedPanelItem = IDynamicPanelItem;

export interface IDetailPanelItem extends IPanelItem {
    startsAsExpanded: boolean;
    horizontalPanelItems?: IHorizontalPanelItem[];
}

export interface IHorizontalPanelItem extends IPanelItem {
    columnSpan: number;
}

export interface IControlPanel {
    rows: number;
    columns: number;
    controls: IControl[];
    userControl: IUserControl | null;
}

export enum EUserControlParametersDataType {
    'Integer' = 'Integer',
    'Number' = 'Number',
    'String' = 'String',
    'Boolean' = 'Boolean',
    'Select' = 'Select',
}

export interface IUserControlParameters {
    name: string;
    description: string;
    dataType: EUserControlParametersDataType;
    selectData?: IUserControlParametersSelectDataItem[];
}

export interface IUserControlParametersSelectDataItem {
    id: string;
    description: string;
}

export interface IUserControl extends Omit<IUserControlDTO, 'jsonParameters'> {
    jsonParameters: IUserControlParameters[];
}

/**
 * Oggetti Server.
 */
export interface ICustomPageTypeConstraintDTO {
    idType: string;
    jsonConstraint: IServerConstraint;
}

export interface ICustomPageTypeItemsSourceDTO {
    idType: string;
    jsonItemsSource: IServerDataSource;
}

export interface ICustomPageLayoutDTO {
    id: string;
    jsonConstraint?: IServerConstraint;
    jsonPanel: ICustomPage;
}

export interface IServerConstraint {
    array?: IConstraint[];
}

export interface ICustomPageTypeDefaultValueDTO {
    idType: string;
    jsonDefaultValue: IPlaceholders;
}

export interface ICustomPageLayoutMultiReportDTO {
    id: string;
    idGroup: string;
}

export interface IUserControlDTO {
    id: string;
    description: string;
    nameId: string;
    jsonParameters: string;
}
export interface IAdditionalSheetSettings {
    target: string; //binding (advanced Array),
    mappingList: string; // lsListName
    foreignKeys: IAdditionalSheetForeignKey[]; // binding (source)
}
export interface IAdditionalSheetForeignKey {
    key: string;
    binding: string;
}
